import type { PreparedSceneAsset } from './sceneLayerAsset'
import { MAX_ATTEMPTS, readData, uploadRequest } from './uploadRequest'

// Keep every request well below the Worker body/memory limits. R2 requires
// multipart parts of at least 5 MiB, except for the last part.
const PART_BYTES = 8 * 1024 * 1024

interface SceneUploadOptions {
  apiBase: string
  uploadId: string
  token: string | null
  adminEmail: string | null | undefined
  onStatus?: (message: string) => void
}

/** Upload a prepared SPZ (or original PLY after conversion failure).
 * Large assets use sequential, retryable R2 parts without another full-file
 * ArrayBuffer. Completion publishes the new object only after all parts arrive.
 */
export async function uploadSceneAsset(
  prepared: PreparedSceneAsset,
  { apiBase, uploadId, token, adminEmail, onStatus }: SceneUploadOptions,
): Promise<string> {
  const body = prepared.body instanceof Blob
    ? prepared.body
    : new Blob([prepared.body], { type: 'application/octet-stream' })
  if (body.size === 0) throw new Error('Scene layer file is empty')
  const partCount = Math.ceil(body.size / PART_BYTES)
  if (partCount > 10_000) throw new Error('Scene layer exceeds the multipart upload size limit')

  const base = `${apiBase.replace(/\/$/, '')}/api/v2/admin/models/proxy/upload/${encodeURIComponent(uploadId)}`
  const path = `static/scene.${prepared.ext}`
  const key = `models/${uploadId}/${path}`
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(adminEmail ? { 'X-Admin-Email': adminEmail } : {}),
  }
  const binaryHeaders = { ...headers, 'Content-Type': 'application/octet-stream' }
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' }
  const label = `scene.${prepared.ext}`
  onStatus?.(`Uploading ${label} (${(body.size / 1e6).toFixed(2)} MB)…`)

  if (body.size <= PART_BYTES) {
    await uploadRequest(`${base}/file/${path}`, {
      method: 'PUT', headers: binaryHeaders, body,
    }, 'Scene upload', (attempt) => onStatus?.(`Retrying ${label} upload (${attempt}/${MAX_ATTEMPTS})…`))
    return key
  }

  const start = await uploadRequest(`${base}/scene/multipart`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ ext: prepared.ext }),
  }, 'Starting scene upload')
  const { multipartUploadId } = await readData<{ multipartUploadId: string }>(start, 'Starting scene upload')
  if (typeof multipartUploadId !== 'string' || !multipartUploadId) {
    throw new Error('Starting scene upload failed: missing multipart upload id')
  }
  const session = `${base}/scene/multipart/${encodeURIComponent(multipartUploadId)}`
  const parts: { partNumber: number; etag: string }[] = []

  try {
    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const completedBytes = (partNumber - 1) * PART_BYTES
      onStatus?.(`Uploading ${label}… ${Math.floor(completedBytes / body.size * 100)}% (part ${partNumber}/${partCount})`)
      const response = await uploadRequest(`${session}/part/${partNumber}?ext=${prepared.ext}`, {
        method: 'PUT',
        headers: binaryHeaders,
        body: body.slice(completedBytes, completedBytes + PART_BYTES),
      }, `Scene upload part ${partNumber}/${partCount}`, (attempt) => {
        onStatus?.(`Retrying ${label} part ${partNumber}/${partCount} (${attempt}/${MAX_ATTEMPTS})…`)
      })
      const part = await readData<{ partNumber: number; etag: string }>(response, `Scene upload part ${partNumber}`)
      if (part.partNumber !== partNumber || typeof part.etag !== 'string' || !part.etag) {
        throw new Error(`Scene upload part ${partNumber} failed: invalid server response`)
      }
      parts.push(part)
    }

    onStatus?.(`Finishing ${label} upload… 100%`)
    const response = await uploadRequest(`${session}/complete`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify({ ext: prepared.ext, parts }),
    }, 'Completing scene upload')
    const completed = await readData<{ key: string }>(response, 'Completing scene upload')
    if (completed.key !== key) {
      throw new Error('Completing scene upload failed: invalid object key')
    }
    return key
  } catch (error) {
    // Best effort: preserve the useful original error if cleanup also fails.
    await fetch(`${session}?ext=${prepared.ext}`, { method: 'DELETE', headers }).catch(() => {})
    throw error
  }
}
